import React, { useState } from 'react';
import { 
  FileText, Upload, Wand2, Download, Loader2, 
  CheckCircle, AlertTriangle, ChevronDown, ChevronUp,
  Eye, RefreshCw, Sparkles
} from 'lucide-react';
import api, { documentsAPI } from '../../services/api';

const WidgetFormulaires = ({ dossierId, dossier, user }) => {
  const [expanded, setExpanded] = useState(true);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [mappedFields, setMappedFields] = useState({});
  const [generatedPdf, setGeneratedPdf] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setAnalysisResult(null);
      setMappedFields({});
      setGeneratedPdf(null);
      setError(null);
    } else {
      setError('Veuillez sélectionner un fichier PDF');
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dossier_id', dossierId);
      
      const response = await api.post('/formulaires/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAnalysisResult(response.data);
      
      // Utiliser le mapping proposé par l'IA
      if (response.data.mapping) {
        setMappedFields(response.data.mapping);
      }
      
    } catch (err) {
      console.error('Erreur analyse:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerate = async () => {
    if (!file || !analysisResult) return;
    
    setGenerating(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dossier_id', dossierId);
      formData.append('field_values', JSON.stringify(mappedFields));
      
      const response = await api.post('/formulaires/generate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setGeneratedPdf(pdfUrl);
      
      // Sauvegarder le document
      const saveFormData = new FormData();
      saveFormData.append('file', pdfBlob, `Formulaire_${file.name}`);
      saveFormData.append('dossier_id', dossierId);
      saveFormData.append('type_document', 'Formulaire prérempli');
      saveFormData.append('uploaded_by', user?.id || '');
      saveFormData.append('uploaded_by_nom', user ? `${user.prenom} ${user.nom}` : 'Système');
      
      await documentsAPI.upload(saveFormData);
      
    } catch (err) {
      console.error('Erreur génération:', err);
      setError('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setMappedFields(prev => ({ ...prev, [fieldName]: value }));
  };

  const resetForm = () => {
    setFile(null);
    setAnalysisResult(null);
    setMappedFields({});
    setGeneratedPdf(null);
    setError(null);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header compact */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-teal-50 border-b border-teal-200 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🪄</span>
          <h3 className="font-semibold text-teal-800">Formulaires automatiques</h3>
          <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> IA
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-teal-600" /> : <ChevronDown className="w-4 h-4 text-teal-600" />}
      </div>

      {expanded && (
        <div className="p-4">
          {/* Erreur */}
          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Étape 1: Upload */}
          {!file && (
            <div className="text-center py-4">
              <FileText className="w-10 h-10 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 mb-3">
                Importez un formulaire PDF (ARRCO, CAF, MDPH...)
              </p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition cursor-pointer text-sm">
                <Upload className="w-4 h-4" />
                Sélectionner un PDF
                <input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </label>
            </div>
          )}

          {/* Étape 2: Fichier sélectionné */}
          {file && !analysisResult && (
            <div className="text-center py-2">
              <div className="mb-3 p-3 bg-gray-50 rounded-lg inline-flex items-center gap-3">
                <FileText className="w-6 h-6 text-teal-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-800 text-sm">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              
              <div className="flex justify-center gap-2">
                <button
                  onClick={resetForm}
                  className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Changer
                </button>
                <button
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyse IA...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Analyser avec l'IA
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Étape 3: Résultat de l'analyse */}
          {analysisResult && !generatedPdf && (
            <div>
              {/* Indicateur de confiance */}
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-700">
                  {Object.keys(mappedFields).length} champs mappés
                </span>
                {analysisResult.confidence > 0 && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    Confiance: {Math.round(analysisResult.confidence * 100)}%
                  </span>
                )}
              </div>

              {/* Notes de l'IA */}
              {analysisResult.notes && (
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                  💡 {analysisResult.notes}
                </div>
              )}

              {/* Champs à mapper */}
              <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                {Object.entries(mappedFields).map(([fieldName, value], index) => (
                  <div key={index} className="flex items-center gap-2">
                    <label className="w-1/3 text-xs font-medium text-gray-600 truncate" title={fieldName}>
                      {fieldName}
                    </label>
                    <input
                      type="text"
                      value={value || ''}
                      onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                      className="flex-1 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={resetForm}
                  className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Recommencer
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Génération...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Générer PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Étape 4: PDF généré */}
          {generatedPdf && (
            <div className="text-center py-2">
              <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
              <p className="text-sm font-medium text-green-700 mb-3">PDF généré et ajouté au dossier !</p>
              
              <div className="flex justify-center gap-2">
                <a
                  href={generatedPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4" />
                  Voir
                </a>
                <a
                  href={generatedPdf}
                  download={`Formulaire_prerempli.pdf`}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  Télécharger
                </a>
                <button
                  onClick={resetForm}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4" />
                  Nouveau
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WidgetFormulaires;
